import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import { SnackbarProvider, SnackbarContent, useSnackbar } from 'notistack';
import type { CustomContentProps } from 'notistack';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import Close from '@mui/icons-material/Close';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';

const VARIANT_CONFIG = {
  success: { icon: CheckCircleOutlined, colorKey: 'success' },
  error: { icon: ErrorOutlined, colorKey: 'error' },
  warning: { icon: WarningAmberOutlined, colorKey: 'warning' },
  info: { icon: InfoOutlined, colorKey: 'info' },
  default: { icon: InfoOutlined, colorKey: 'info' }
} as const;

const NeutralSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message, variant = 'default' }, ref) => {
    const { closeSnackbar } = useSnackbar();
    const theme = useTheme();

    /* c8 ignore next */
    const config = VARIANT_CONFIG[variant as keyof typeof VARIANT_CONFIG] ?? VARIANT_CONFIG.default;
    const IconComponent = config.icon;
    const accentColor = theme.palette[config.colorKey as keyof typeof theme.palette] as any;

    return (
      <SnackbarContent ref={ref}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 288,
            maxWidth: 400,
            px: 2,
            py: 1.5,
            borderRadius: 1,
            /* c8 ignore next */
            bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.900',
            boxShadow: 'none'
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
            <IconComponent
              sx={{
                fontSize: 18,
                flexShrink: 0,
                /* c8 ignore next */
                color: accentColor?.main ?? theme.palette.primary.main
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: 'common.white',
                fontWeight: (theme) => theme.typography.fontWeightSemiBold,
                lineHeight: 1.4,
                minWidth: 0
              }}
            >
              {message}
            </Typography>
          </Stack>
          <IconButton
            size="small"
            onClick={() => closeSnackbar(id)}
            sx={{ ml: 1, flexShrink: 0, color: 'grey.500', '&:hover': { color: 'grey.300' } }}
          >
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </SnackbarContent>
    );
  }
);

NeutralSnackbar.displayName = 'NeutralSnackbar';

function NotistackProvider({ children }: { children: ReactNode }) {
  return (
    <SnackbarProvider
      dense
      maxSnack={3}
      preventDuplicate
      autoHideDuration={4000}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      Components={{
        success: NeutralSnackbar,
        error: NeutralSnackbar,
        warning: NeutralSnackbar,
        info: NeutralSnackbar,
        default: NeutralSnackbar
      }}
    >
      {children}
    </SnackbarProvider>
  );
}

export default NotistackProvider;
